import { useState } from "react";

import { ApiError } from "@/api/client";
import { useCall } from "@/api/commerce";
import { useUsers } from "@/api/hooks";
import { useAssets, type Coverage, type Priority, type TicketKind, type TicketStatus, type TicketSummary } from "@/api/service";
import { Badge } from "@/components/crm/Badge";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Alert, Modal } from "@/components/ui";

export const STATUS: Record<TicketStatus, { label: string; tone: "neutral" | "brand" | "ok" | "danger" | "warn" }> = {
  open: { label: "Open", tone: "brand" }, scheduled: { label: "Visit scheduled", tone: "brand" }, in_progress: { label: "In progress", tone: "warn" },
  waiting_parts: { label: "Waiting for parts", tone: "warn" }, resolved: { label: "Resolved", tone: "ok" }, closed: { label: "Closed", tone: "neutral" },
};
export const PRIORITY: Record<Priority, { label: string; tone: "neutral" | "brand" | "danger" | "warn" }> = {
  urgent: { label: "Urgent", tone: "danger" }, high: { label: "High", tone: "warn" }, normal: { label: "Normal", tone: "brand" }, low: { label: "Low", tone: "neutral" },
};
export const KIND: Record<TicketKind, string> = {
  breakdown: "Breakdown", preventive: "Preventive maintenance", installation: "Installation", complaint: "Complaint", amc_visit: "AMC visit",
};
export const COVER: Record<Coverage["kind"], { label: string; tone: "ok" | "brand" | "neutral" }> = {
  warranty: { label: "Warranty", tone: "ok" }, amc: { label: "AMC", tone: "brand" }, chargeable: { label: "Chargeable", tone: "neutral" },
};

export const StatusBadge = ({ s }: { s: TicketStatus }) => <Badge tone={STATUS[s].tone}>{STATUS[s].label}</Badge>;
export const PriorityBadge = ({ p }: { p: Priority }) => <Badge tone={PRIORITY[p].tone}>{PRIORITY[p].label}</Badge>;
export const CoverBadge = ({ c }: { c: Coverage["kind"] }) => <Badge tone={COVER[c].tone}>{COVER[c].label}</Badge>;

export const errorText = (e: unknown, fallback = "Could not save") =>
  e instanceof ApiError ? (e.problem.errors?.map((x) => `${x.field}: ${x.message}`).join("; ") || e.message) : fallback;

/** Open a ticket: for a customer and one of their machines, or from an inbox conversation. */
export function NewTicketDialog({ onClose, onCreated, account, conversationId, contactId, subject: startSubject }: {
  onClose: () => void; onCreated: (t: TicketSummary) => void; account?: PickerValue | null; conversationId?: string; contactId?: string; subject?: string;
}) {
  const save = useCall<TicketSummary>("tickets", "ticket-counts", "timeline", "assets");
  const users = useUsers();
  const [customer, setCustomer] = useState<PickerValue | null>(account ?? null);
  const machines = useAssets({ account_id: customer?.id }, !!customer);
  const [asset, setAsset] = useState("");
  const [subject, setSubject] = useState(startSubject ?? "");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<Priority>("normal");
  const [kind, setKind] = useState<TicketKind>("breakdown");
  const [assignee, setAssignee] = useState("");
  const [error, setError] = useState<string | null>(null);
  const chosen = machines.data?.find((m) => m.id === asset);
  const submit = (e: React.FormEvent) => {
    e.preventDefault(); setError(null);
    save.mutateAsync({ path: "/tickets", body: {
      account_id: customer?.id ?? null, asset_id: asset || null, conversation_id: conversationId ?? null, contact_id: contactId ?? null,
      subject, description: description || null, priority, kind, assignee_id: assignee || null,
    } }).then(onCreated).catch((err) => setError(errorText(err)));
  };
  return (
    <Modal title="New service ticket" onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        {error && <Alert>{error}</Alert>}
        {!conversationId || !account ? <RecordPicker label="Customer" endpoint="/accounts" value={customer} onChange={(v) => { setCustomer(v); setAsset(""); }} />
          : <p className="text-sm">Customer: <b>{account.name}</b></p>}
        {customer && (
          <div><label htmlFor="nt-asset" className="label">Machine</label>
            <select id="nt-asset" className="input" value={asset} onChange={(e) => setAsset(e.target.value)}>
              <option value="">Not about one machine</option>
              {machines.data?.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.serial_number}</option>)}
            </select>
            {chosen?.coverage && <p className="mt-1 text-xs"><CoverBadge c={chosen.coverage.kind} /> <span className="text-muted">{chosen.coverage.label}</span></p>}
          </div>
        )}
        <div><label htmlFor="nt-subject" className="label">Problem</label><input id="nt-subject" className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Spindle vibrates above 1200 rpm" /></div>
        <div><label htmlFor="nt-desc" className="label">Details</label><textarea id="nt-desc" className="input min-h-20" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div><label htmlFor="nt-priority" className="label">Priority</label>
            <select id="nt-priority" className="input" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {(Object.keys(PRIORITY) as Priority[]).map((p) => <option key={p} value={p}>{PRIORITY[p].label}</option>)}</select></div>
          <div><label htmlFor="nt-kind" className="label">Type</label>
            <select id="nt-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as TicketKind)}>
              {(Object.keys(KIND) as TicketKind[]).filter((k) => k !== "amc_visit").map((k) => <option key={k} value={k}>{KIND[k]}</option>)}</select></div>
          <div><label htmlFor="nt-engineer" className="label">Engineer</label>
            <select id="nt-engineer" className="input" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Not yet</option>
              {users.data?.filter((u) => u.is_active && u.role.key !== "dealer").map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}</select></div>
        </div>
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={save.isPending || !subject.trim() || (!customer && !conversationId)}>Open ticket</button></div>
      </form>
    </Modal>
  );
}
